import { useCallback, useEffect, useState } from 'react';
import {
  apiAuth,
  apiUsers,
  beginImpersonation,
  clearToken,
  endImpersonation,
  isImpersonating,
  setToken,
  NATIVE_AUTH_LOST_EVENT,
} from '../api/client.js';
import { IS_NATIVE } from '../native/platform.js';
import { resetAllFilters } from './useFilterStore.js';
import { currentPathForLogin } from '../lib/routes.js';

function consumeTokenFromHash() {
  const hash = window.location.hash;
  if (!hash || !hash.startsWith('#')) return false;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get('token');
  if (!token) return false;
  setToken(token);
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return true;
}

// Codes d'erreur OAuth opaques renvoyés par le backend (F-AUTH-03). Le détail
// (domaine refusé, compte désactivé, state CSRF, etc.) reste dans les logs
// serveur — côté front, message générique unique pour ne pas permettre
// d'énumération.
const AUTH_ERROR_MESSAGES = {
  oauth_failed:
    "Connexion impossible. Vérifie que tu utilises bien un compte Synotis ou contacte un administrateur.",
};

function consumeAuthErrorFromQuery() {
  const params = new URLSearchParams(window.location.search);
  const err = params.get('auth_error');
  if (!err) return null;
  params.delete('auth_error');
  const newSearch = params.toString();
  window.history.replaceState(
    null,
    '',
    window.location.pathname + (newSearch ? `?${newSearch}` : ''),
  );
  return AUTH_ERROR_MESSAGES[err] ?? AUTH_ERROR_MESSAGES.oauth_failed;
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Recharge l'utilisateur depuis /auth/me (status/role à jour côté BDD).
  // Lot C — on tente TOUJOURS /auth/me : la session normale passe par le cookie
  // SSO (`syn_session`, pas de token local). Un token local n'existe que pour
  // la voie legacy + l'impersonation (Sprint 18).
  const refresh = useCallback(async () => {
    try {
      const { user: fresh } = await apiAuth.me();
      setUser(fresh);
      return fresh;
    } catch {
      // Ne pas purger le token ici : `apiFetch` gère déjà l'état du token sur un
      // 401 (et préserve le token admin en cas d'impersonation — F-S18-02). On
      // se contente de réinitialiser l'UI.
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const errFromQuery = consumeAuthErrorFromQuery();
    if (errFromQuery) setAuthError(errFromQuery);

    consumeTokenFromHash();

    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Bascule automatique : tant que le compte est PENDING, on interroge /auth/me
  // périodiquement. Dès que l'admin l'active, `user.status` passe à ACTIVE et
  // l'application s'affiche — sans reconnexion ni rechargement manuel.
  useEffect(() => {
    if (user?.status !== 'PENDING') return undefined;
    const id = setInterval(() => {
      refresh();
    }, 10000);
    return () => clearInterval(id);
  }, [user?.status, refresh]);

  // Lot C — redirige vers le SSO core (auth centralisée + cookie cross-subdomain).
  // Ticket #39 — sans `next` explicite, on repart sur le chemin courant : arriver
  // sur `/schedule` avec une session expirée doit ramener sur `/schedule` après
  // login, pas sur l'accueil. La query string est volontairement écartée —
  // `safeNext` côté API (F-AUTH-02) n'accepte qu'un pathname.
  const login = useCallback((next) => {
    // App native : la connexion est portée par NativeGate (PKCE + navigateur
    // système) — on y retourne au lieu de rediriger vers le SSO web.
    if (IS_NATIVE) {
      window.dispatchEvent(new CustomEvent(NATIVE_AUTH_LOST_EVENT));
      return;
    }
    window.location.href = apiAuth.loginUrl(next ?? currentPathForLogin());
  }, []);

  const logout = useCallback(async () => {
    // App native : révocation du refresh token chez synocore puis purge du
    // Keychain/Keystore, et retour à l'écran de connexion natif.
    if (IS_NATIVE) {
      try {
        // #93 — retirer le jeton push AVANT la révocation (le Bearer sert encore).
        const { unregisterNativePush } = await import('../native/nativePush.js');
        await unregisterNativePush();
      } catch {
        // best-effort
      }
      try {
        const { signOut } = await import('../native/nativeAuth.js');
        await signOut();
      } catch {
        // best-effort : la purge locale est faite par signOut
      }
      setUser(null);
      window.dispatchEvent(new CustomEvent(NATIVE_AUTH_LOST_EVENT));
      return;
    }
    try {
      // F-S18-03 — Si une impersonation est en cours, tracer le STOP (auteur =
      // admin réel) AVANT de purger : ça évite un START sans STOP dans le journal
      // quand l'admin se déconnecte au lieu de cliquer "Revenir".
      if (isImpersonating()) {
        await apiAuth.stopImpersonation().catch(() => {});
      }
      await apiAuth.logout();
    } catch {
      // token déjà invalide — on continue
    }
    clearToken();
    setUser(null);
  }, []);

  // Impersonation — un ADMIN bascule sur l'identité d'un autre compte. Le token
  // admin réel est conservé localement pour pouvoir revenir.
  const startImpersonation = useCallback(async (targetId) => {
    const { token } = await apiUsers.impersonate(targetId);
    beginImpersonation(token);
    // Changement d'identité : purger les filtres facettés persistés en
    // localStorage (sinon un « Personne est X » posé sous l'ancienne identité
    // fausse la vue) PUIS recharger entièrement la page. Le rechargement garantit
    // la réinitialisation de TOUS les caches module-level liés à l'identité
    // (filtres en mémoire, cache /auth/me de la FilterBar, etc.) — plus robuste
    // qu'une invalidation cache par cache.
    resetAllFilters();
    window.location.reload();
  }, []);

  const stopImpersonation = useCallback(async () => {
    // Trace l'audit côté backend AVANT de restaurer le token admin (l'auteur
    // réel n'est lisible que tant qu'on est encore sur le token d'impersonation).
    try {
      await apiAuth.stopImpersonation();
    } catch {
      // best-effort — on revient au compte admin de toute façon
    }
    endImpersonation();
    // Retour à l'identité admin : purger les filtres + rechargement complet
    // (cf. startImpersonation : réinitialise tous les caches d'identité).
    resetAllFilters();
    window.location.reload();
  }, []);

  return {
    user,
    loading,
    authError,
    login,
    logout,
    refresh,
    startImpersonation,
    stopImpersonation,
    isImpersonating: Boolean(user?.impersonatedBy),
    isAuthenticated: Boolean(user),
  };
}
